import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, InputAdornment,
  Divider, CircularProgress, Alert, Snackbar, Chip, Avatar,
  List, ListItem, ListItemAvatar, ListItemText, ListItemButton,
  Paper, Fade, Skeleton
} from '@mui/material';
import DownloadIcon     from '@mui/icons-material/Download';
import FileTextIcon     from '@mui/icons-material/Description';
import CalendarIcon     from '@mui/icons-material/CalendarToday';
import PhoneIcon        from '@mui/icons-material/Phone';
import PersonIcon       from '@mui/icons-material/Person';
import FilterIcon       from '@mui/icons-material/FilterList';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import SearchIcon       from '@mui/icons-material/Search';
import InfoIcon         from '@mui/icons-material/Info';
import Api from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

const ExportTransactions = ({ transactions }) => {
  const { palette, Colors } = useAppTheme();
  const [exportType,   setExportType]   = useState('dateRange');
  const [phoneNumber,  setPhoneNumber]  = useState('');
  const [customerName, setCustomerName] = useState('');
  const [startDate,    setStartDate]    = useState('');
  const [endDate,      setEndDate]      = useState('');
  const [loading,      setLoading]      = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [allTenants,    setAllTenants]   = useState([]);
  const [showNotFoundInfo, setShowNotFoundInfo] = useState(false);
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    Api.getTenantSelection().then(setAllTenants).catch(console.error);
  }, []);

  const debounce = (fn, wait) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const searchByPhone = useCallback(debounce(async (phone) => {
    if (!phone || phone.length < 3) { setSearchResults([]); setShowNotFoundInfo(false); return; }
    setSearchLoading(true);
    try {
      const tenant = await Api.getTenantByPhone(phone);
      if (tenant) { setSearchResults([tenant]); setShowNotFoundInfo(false); }
      else { setSearchResults([]); setShowNotFoundInfo(true); }
    } catch { setSearchResults([]); setShowNotFoundInfo(true); }
    finally { setSearchLoading(false); }
  }, 500), []);

  const searchByName = useCallback(debounce((name) => {
    if (!name || name.length < 2) { setSearchResults([]); setShowNotFoundInfo(false); return; }
    const filtered = allTenants.filter(t =>
      t.name?.toLowerCase().includes(name.toLowerCase()) ||
      `${t.first_name} ${t.last_name}`.toLowerCase().includes(name.toLowerCase())
    );
    setSearchResults(filtered);
    setShowNotFoundInfo(filtered.length === 0);
  }, 300), [allTenants]);

  useEffect(() => {
    if (exportType === 'phone' && phoneNumber) searchByPhone(phoneNumber);
    else if (exportType === 'phone') { setSearchResults([]); setShowNotFoundInfo(false); }
  }, [phoneNumber, exportType]);

  useEffect(() => {
    if (exportType === 'name' && customerName) searchByName(customerName);
    else if (exportType === 'name') { setSearchResults([]); setShowNotFoundInfo(false); }
  }, [customerName, exportType]);

  const resetSearch = () => { setPhoneNumber(''); setCustomerName(''); setStartDate(''); setEndDate(''); setSelectedTenant(null); setSearchResults([]); setShowNotFoundInfo(false); };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    if (exportType === 'phone') setPhoneNumber(tenant.phone_number || tenant.phone);
    else setCustomerName(tenant.name || `${tenant.first_name} ${tenant.last_name}`);
    setSearchResults([]); setShowNotFoundInfo(false);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      let exportData, filename;
      if (exportType === 'phone') {
        if (!phoneNumber) throw new Error('Please enter a phone number');
        const phone = selectedTenant?.phone_number || selectedTenant?.phone || phoneNumber;
        exportData = await Api.exportTransactionsByPhone(phone);
        filename = `transactions_${phone}_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (exportType === 'name') {
        if (!customerName) throw new Error('Please enter a customer name');
        const name = selectedTenant ? (selectedTenant.name || `${selectedTenant.first_name} ${selectedTenant.last_name}`) : customerName;
        exportData = await Api.exportTransactionsByName(name);
        filename = `transactions_${name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        if (!startDate || !endDate) throw new Error('Please select both start and end dates');
        if (new Date(startDate) > new Date(endDate)) throw new Error('Start date must be before end date');
        exportData = await Api.exportTransactionsByDateRange(startDate, endDate);
        filename = `transactions_${startDate}_to_${endDate}.csv`;
      }
      const blob = new Blob([exportData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob); link.download = filename; link.click();
      URL.revokeObjectURL(link.href);
      setSnackbar({ open: true, message: 'Export completed successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Export failed', severity: 'error' });
    } finally { setLoading(false); }
  };

  const dropdownPaper = { backgroundColor: palette.background.secondary, border: `1px solid ${palette.border}` };

  const TenantDropdown = () => searchResults.length > 0 && (
    <Fade in>
      <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: 250, overflow: 'auto', mt: 1, ...dropdownPaper }}>
        <List dense>
          {searchResults.map((tenant, i) => {
            const name  = tenant.name || `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim();
            const phone = tenant.phone_number || tenant.phone || 'No phone';
            return (
              <ListItem key={i} disablePadding>
                <ListItemButton onClick={() => handleTenantSelect(tenant)}
                  sx={{ '&:hover': { backgroundColor: palette.background.tertiary } }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: Colors.primary.main, width: 32, height: 32, fontSize: '0.85rem' }}>
                      {name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: palette.text.primary }}>{name}</Typography>
                        {tenant.unit_number && <Chip label={`Unit ${tenant.unit_number}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18, borderColor: palette.border, color: palette.text.secondary }} />}
                      </Box>
                    }
                    secondary={<Typography variant="body2" sx={{ color: palette.text.secondary }}>📞 {phone}{tenant.email ? ` • ✉️ ${tenant.email}` : ''}</Typography>}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Paper>
    </Fade>
  );

  const NotFoundInfo = () => showNotFoundInfo ? (
    <Fade in>
      <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, mt: 1, p: 2, backgroundColor: `${Colors.status.warning}15`, border: `1px solid ${Colors.status.warning}40` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: Colors.status.warning, fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>
            No registered tenant found, but transactions may still exist.
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: palette.text.secondary }}>
          Click "Export" to search for transactions with this {exportType === 'phone' ? 'phone number' : 'name'}.
        </Typography>
      </Paper>
    </Fade>
  ) : null;

  return (
    <Card elevation={0} sx={{ mb: 3, backgroundColor: palette.background.secondary, border: `1px solid ${palette.border}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FileTextIcon sx={{ mr: 1, color: Colors.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: Colors.primary.main }}>Export Payment Statements</Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Export type */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Export Type</InputLabel>
              <Select value={exportType} label="Export Type" onChange={e => { setExportType(e.target.value); resetSearch(); }}
                startAdornment={<FilterIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />}>
                {[['dateRange', <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />, 'Date Range'],
                  ['phone',    <PhoneIcon    sx={{ mr: 1, fontSize: 20 }} />, 'By Phone Number'],
                  ['name',     <PersonIcon   sx={{ mr: 1, fontSize: 20 }} />, 'By Customer Name']].map(([val, icon, label]) => (
                  <MenuItem key={val} value={val}><Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}{label}</Box></MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Dynamic input */}
          <Grid item xs={12} md={6}>
            {exportType === 'phone' && (
              <Box sx={{ position: 'relative' }}>
                <TextField fullWidth label="Phone Number" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)}
                  placeholder="e.g., 254701234567"
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment>, endAdornment: searchLoading && <InputAdornment position="end"><CircularProgress size={20} sx={{ color: Colors.primary.main }} /></InputAdornment> }} />
                <TenantDropdown /><NotFoundInfo />
              </Box>
            )}
            {exportType === 'name' && (
              <Box sx={{ position: 'relative' }}>
                <TextField fullWidth label="Customer Name" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Enter customer name"
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment>, endAdornment: <InputAdornment position="end"><SearchIcon /></InputAdornment> }} />
                <TenantDropdown /><NotFoundInfo />
              </Box>
            )}
            {exportType === 'dateRange' && (
              <Grid container spacing={2}>
                {[['Start Date', startDate, setStartDate], ['End Date', endDate, setEndDate]].map(([label, val, setter]) => (
                  <Grid item xs={6} key={label}>
                    <TextField fullWidth label={label} type="date" value={val} onChange={e => setter(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><CalendarIcon /></InputAdornment> }} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Export button */}
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={handleExport} disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
              sx={{ minHeight: 56, background: `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})`, '&:hover': { background: `linear-gradient(135deg, ${Colors.primary.main}, ${Colors.primary.light})` } }}>
              {loading ? 'Exporting...' : 'Export'}
            </Button>
          </Grid>
        </Grid>

        {/* Selected tenant */}
        {selectedTenant && (
          <Fade in>
            <Box sx={{ mt: 2 }}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: `${Colors.primary.main}10`, border: `1px solid ${Colors.primary.main}40` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: Colors.primary.main }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: palette.text.primary }}>
                      Selected: {selectedTenant.name || `${selectedTenant.first_name} ${selectedTenant.last_name}`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                      Phone: {selectedTenant.phone_number || selectedTenant.phone}
                      {selectedTenant.unit_number && ` • Unit: ${selectedTenant.unit_number}`}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => setSelectedTenant(null)} sx={{ ml: 'auto', color: palette.text.secondary }}>Clear</Button>
                </Box>
              </Paper>
            </Box>
          </Fade>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default ExportTransactions;